'use client';

import { GoogleTagManager as GtmComponent } from '@next/third-parties/google';
import Script from 'next/script';
import { useContext, useEffect } from 'react';
import { onCLS, onINP, onLCP } from 'web-vitals';
import { useAsPath } from '@/app_lib/hooks/useAsPath';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { UserContext } from '@/app_lib/providers/UserProvider';
import type { EventData, GtagEvent, GtagObject, PageViewDataLayerObject } from '@/app_lib/types/Gtm';
import { InitPageStatus } from './InitPageStatus';
import { GtmPageView, pushToDataLayer, trackEvent } from './utils';

export interface PostMessageData {
  type: string;
  trackingData: EventData;
}

export interface PostMessage {
  data: PostMessageData;
}

export interface GoogleTagManagerProps {
  dataLayer: PageViewDataLayerObject;
}

/** Note:
 * The script tag is different from the original in that it sets the dataLayer to an empty array before loading the GTM script.
 * window.dataLayer will always be undefined whenever we call this script. So the workaround is to use
 * the 'beforeInteractive' strategy to ensure that the dataLayer is initialized so events can be pushed to it.
 */
export const GoogleTagManager = (props: React.PropsWithChildren<GoogleTagManagerProps>) => {
  const { dataLayer } = props;
  const asPath = useAsPath();
  const { setPageData } = useContext(GtmContext);
  const { userAccess } = useContext(UserContext);

  useEffect(() => {
    if (!userAccess.data?.email) {
      return;
    }
    fetch('/api/updateLastSiteActivityDate', {
      body: JSON.stringify({
        email: userAccess.data?.email,
      }),
      method: 'POST',
    });
  }, [userAccess.data?.email]);

  useEffect(() => {
    const trackWebVitals = ({ name, delta, id }: GtagEvent) => {
      const ClsToMillisecondsFactor = 1000;
      const gtagObject: GtagObject = {
        event: name,
        eventAction: name,
        eventCategory: 'Web Vitals',
        eventLabel: id,
        eventValue: Math.round(name === 'CLS' ? delta * ClsToMillisecondsFactor : delta),
        nonInteraction: 1,
      };
      pushToDataLayer(gtagObject);
    };

    onCLS(trackWebVitals);
    onINP(trackWebVitals);
    onLCP(trackWebVitals);
  }, []);

  useEffect(() => {
    const handleGtmMessage = (e: PostMessage) => {
      if (e.data && e.data.type === 'gtm') {
        trackEvent(e.data.trackingData);
      }
    };

    window.addEventListener('message', handleGtmMessage);

    return () => {
      window.removeEventListener('message', handleGtmMessage);
    };
  }, []);

  useEffect(() => {
    if (asPath) {
      const pageViewData = {
        ...dataLayer,
        history: [],
        path: asPath,
      };
      GtmPageView(pageViewData);
      setPageData(dataLayer);
    }
  }, [asPath, dataLayer]);

  const GtmId = process.env.GTM_ID;
  const GtmAuth = process.env.GTM_AUTH;
  const GtmPreview = process.env.GTM_PREVIEW;
  const initDataLayer: { [key: string]: any } = { ...dataLayer };

  return (
    <>
      <Script
        id='gtm-consent-defaults'
        strategy='beforeInteractive'
      >
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('consent', 'default', {
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied',
            'analytics_storage': 'denied',
            'functionality_storage': 'denied',
            'personalization_storage': 'denied',
            'security_storage': 'granted',
            'wait_for_update': 500
          });
        `}
      </Script>
      {GtmId && GtmAuth && GtmPreview && (
        <>
          <GtmComponent
            auth={GtmAuth}
            dataLayer={initDataLayer}
            gtmId={GtmId}
            preview={GtmPreview}
          />
          <noscript>
            {/** biome-ignore lint/nursery/noRestrictedElements: this should be a hidden iframe */}
            <iframe
              className=''
              height='0'
              src={`https://www.googletagmanager.com/ns.html?id=${GtmId}&gtm_auth=${GtmAuth}&gtm_preview=${GtmPreview}`}
              style={{
                display: 'none',
                visibility: 'hidden',
              }}
              title='fortune-gtm'
              width='0'
            />
          </noscript>
          <InitPageStatus />
        </>
      )}
    </>
  );
};
